import { useEffect, useRef, useState } from "react";
import { ArrowUp, X, Sparkles, MessageCircleQuestion } from "lucide-react";

// Approved, public LaunchRadar FAQ knowledge base. The assistant answers ONLY
// from this content — it never implies access to a visitor's private/logged-in data.
const FAQ = [
  { k: ["launchradar", "about", "platform", "overview", "product"], q: "What does LaunchRadar do?",
    a: "LaunchRadar turns daily news into project-specific intelligence. It continuously searches for relevant developments for each project, scores and explains every result against that project's goals, and surfaces trends and strategic recommendations so your team knows what changed, why it matters and where to focus." },
  { k: ["who", "for", "audience", "team", "customer"], q: "Who is LaunchRadar for?",
    a: "It's built for organisations and teams that need to stay ahead of developments affecting their work — strategy, policy, market and business-development teams in areas like energy, infrastructure and other policy-driven sectors." },
  { k: ["project", "projects", "work", "setup", "context"], q: "How do projects work?",
    a: "You set up your organisation and a project once — describing your goals, sectors and the organisations or sources you care about. LaunchRadar then continuously brings relevant developments into that project's intelligence, scored and explained against what matters to you." },
  { k: ["collect", "collected", "source", "sources", "gather", "monitor", "monitoring", "frequency", "update", "often", "daily"], q: "How is intelligence collected and how often?",
    a: "LaunchRadar analyses relevant public reporting continuously and refreshes your project intelligence every day. Duplicate links and repeated stories are consolidated so you focus on developments rather than noise." },
  { k: ["score", "scoring", "scored", "relevant", "relevance", "decide", "rank", "work", "works"], q: "How does LaunchRadar decide what's relevant?",
    a: "Every result is scored 0–100 and interpreted against your project's goals and context, with a clear explanation of how it connects to your organisation. Strategic recommendations appear only when a development has a meaningful impact." },
  { k: ["different", "difference", "news", "alert", "alerts", "google"], q: "How is this different from a news alert?",
    a: "News alerts just forward keyword matches. LaunchRadar scores and explains each development against your specific project, consolidates duplicates, tracks trends, and recommends a next step when something truly matters — with citations back to the sources." },
  { k: ["analysis", "ask", "question", "insight", "insights", "conversation", "brief"], q: "What can the analysis do?",
    a: "You can use AI to explore your collected intelligence — ask what changed, compare business implications, or build a brief your team can use, always with sources you can check." },
  { k: ["recommendation", "recommendations", "strategic", "action", "next step"], q: "What are strategic recommendations?",
    a: "When a development has a meaningful impact on your organisation and its projects, LaunchRadar suggests a concrete recommended next step — not a generic 'read later' note." },
  { k: ["trend", "trends", "entities", "entity", "keywords", "momentum"], q: "What do trends show?",
    a: "Trends show coverage momentum, rising keywords, the most-mentioned organisations and the sources shaping the conversation for your project — so you see the pattern, not just individual stories." },
  { k: ["tender", "tenders", "bid", "procurement"], q: "Does it cover tenders?",
    a: "Tender (bids & procurement) intelligence is coming soon — identifying relevant opportunities, deadlines and recommendations when an opportunity warrants attention." },
  { k: ["regulation", "regulatory", "policy", "compliance"], q: "Does it cover regulations?",
    a: "Regulatory (policy) intelligence is coming soon — understanding policy changes, the organisations affected and what each development could mean for your project. News intelligence is live today." },
  { k: ["data", "privacy", "safe", "secure", "private"], q: "What data does the assistant use?",
    a: "On this page I answer general product questions from LaunchRadar's public information only. I can't see any private projects or logged-in data from here." },
];

const DEMO_KEYS = ["demo", "pricing", "price", "cost", "contact", "sales", "access", "trial", "buy", "waitlist"];

const STARTERS = [
  "Who is LaunchRadar for?",
  "How is this different from a news alert?",
  "How does LaunchRadar decide what is relevant?",
];

function answerFor(text) {
  const t = text.toLowerCase();
  if (DEMO_KEYS.some((k) => t.includes(k))) {
    return { demo: true, a: "For a demo, early access or pricing, the quickest way is to join the LaunchRadar waitlist — the team will reach out to arrange it." };
  }
  let best = null; let bestScore = 0;
  for (const f of FAQ) {
    let s = 0;
    for (const kw of f.k) if (t.includes(kw)) s += 1;
    if (s > bestScore) { bestScore = s; best = f; }
  }
  if (best && bestScore > 0) return { a: best.a };
  return { a: "I can answer questions about what LaunchRadar does, how projects work, how intelligence is collected and scored, trends, analysis, strategic recommendations, news, tenders, regulations, and access. Try one of the starter questions below.", showStarters: true };
}

export default function LandingAssistant({ onRequestDemo, openSignal = 0 }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, text, demo?}
  const [input, setInput] = useState("");
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const pillRef = useRef(null);

  useEffect(() => { if (openSignal > 0) setOpen(true); }, [openSignal]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => { document.removeEventListener("keydown", onKey); clearTimeout(t); };
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => {
    if (!open) pillRef.current?.focus?.();
  }, [open]);

  const send = (raw) => {
    const text = (raw ?? input).trim();
    if (!text) return;
    const res = answerFor(text);
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", text },
      { role: "assistant", text: res.a, demo: res.demo, showStarters: res.showStarters },
    ]);
  };

  return (
    <>
      {/* Collapsed floating pill */}
      {!open && (
        <div className="fixed bottom-6 right-4 sm:right-6 z-40 max-w-[calc(100vw-2rem)]">
          <button
            ref={pillRef}
            type="button"
            onClick={() => setOpen(true)}
            data-testid="landing-assistant-pill"
            aria-label="Ask a question about LaunchRadar"
            className="group flex items-center gap-3 pl-4 pr-1.5 py-1.5 rounded-full bg-white border border-[#E2E8F0] shadow-[0_16px_40px_-12px_rgba(15,31,61,0.28)] hover:shadow-[0_20px_48px_-12px_rgba(15,31,61,0.35)] transition-shadow"
          >
            <Sparkles className="h-4 w-4 text-[#10B981] shrink-0" />
            <span className="text-sm text-[#334155] font-medium whitespace-nowrap">Ask a question about LaunchRadar</span>
            <span className="h-9 w-9 shrink-0 flex items-center justify-center rounded-full bg-[#0F1F3D] text-white group-hover:bg-[#172A4F] transition-colors">
              <ArrowUp className="h-4 w-4" />
            </span>
          </button>
        </div>
      )}

      {/* Expanded conversational panel */}
      {open && (
        <div className="fixed inset-0 z-50" data-testid="landing-assistant-overlay">
          {/* Soft, semi-transparent backdrop — page stays visible */}
          <div
            className="absolute inset-0 bg-[#0F1F3D]/25 backdrop-blur-[2px]"
            onClick={() => setOpen(false)}
            data-testid="landing-assistant-backdrop"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="LaunchRadar assistant"
            onClick={(e) => e.stopPropagation()}
            className="absolute bottom-3 left-1/2 -translate-x-1/2 w-[min(680px,calc(100vw-1.5rem))] flex flex-col"
          >
            {/* Suggestion chips (only before first message) */}
            {messages.length === 0 && (
              <div className="flex flex-col items-start gap-2 mb-3 pl-1" data-testid="landing-assistant-starters">
                {STARTERS.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => send(s)}
                    data-testid={`landing-assistant-starter-${i}`}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#0F1F3D]/85 backdrop-blur text-white text-xs font-medium shadow-md hover:bg-[#0F1F3D] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Conversation card */}
            <div className="rounded-2xl bg-white border border-[#E2E8F0] shadow-[0_30px_80px_-20px_rgba(15,31,61,0.35)] overflow-hidden flex flex-col max-h-[70vh]">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#F1F5F9] bg-white">
                <div className="flex items-center gap-2">
                  <span className="h-7 w-7 rounded-full bg-[#0F1F3D] text-white flex items-center justify-center">
                    <MessageCircleQuestion className="h-4 w-4" />
                  </span>
                  <div>
                    <div className="text-sm font-bold text-[#0F1F3D] leading-none">LaunchRadar assistant</div>
                    <div className="text-[11px] text-[#94A3B8] mt-0.5">Answers about the product</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  data-testid="landing-assistant-close"
                  aria-label="Close assistant"
                  className="p-2 rounded-md hover:bg-[#F1F5F9] text-[#64748B]"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3 min-h-[120px]" data-testid="landing-assistant-messages">
                {messages.length === 0 ? (
                  <p className="text-sm text-[#64748B]">
                    Hi! Ask me anything about how LaunchRadar works — projects, scoring, trends, analysis, recommendations, tenders, regulations or access. Pick a starter question above to begin.
                  </p>
                ) : (
                  messages.map((m, i) => (
                    <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                      {m.role === "user" ? (
                        <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#0F1F3D] text-white px-4 py-2.5 text-sm">{m.text}</div>
                      ) : (
                        <div className="max-w-full rounded-2xl rounded-bl-sm bg-[#F8FAFC] border border-[#E2E8F0] px-4 py-3 text-sm text-[#1E293B]">
                          <p className="leading-relaxed">{m.text}</p>
                          {m.demo && (
                            <button
                              type="button"
                              onClick={() => { setOpen(false); onRequestDemo?.(); }}
                              data-testid="landing-assistant-demo-cta"
                              className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-[#0F1F3D] text-white text-xs font-semibold hover:bg-[#172A4F]"
                            >
                              Join the waitlist
                            </button>
                          )}
                          {m.showStarters && (
                            <div className="mt-3 flex flex-col items-start gap-1.5">
                              {STARTERS.map((s, si) => (
                                <button key={si} type="button" onClick={() => send(s)} className="text-xs font-semibold text-[#0F1F3D] hover:underline">{s}</button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="border-t border-[#F1F5F9] p-3 flex items-center gap-2 bg-white"
              >
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask anything about LaunchRadar…"
                  data-testid="landing-assistant-input"
                  className="flex-1 px-4 py-2.5 rounded-full border border-[#CBD5E1] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20"
                />
                <button
                  type="submit"
                  disabled={!input.trim()}
                  data-testid="landing-assistant-send"
                  aria-label="Send"
                  className="h-10 w-10 shrink-0 flex items-center justify-center rounded-full bg-[#0F1F3D] text-white hover:bg-[#172A4F] disabled:opacity-40 transition-colors"
                >
                  <ArrowUp className="h-4 w-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}